import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { EmptyState } from '../../components/ui/EmptyState.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { Skeleton } from '../../components/ui/Skeleton.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency, formatDate } from '../../utils/formatters.js';

/**
 * Das Journal - die Grundansicht jeder Buchhaltung, und bis jetzt hatte die
 * Anwendung keine.
 *
 * Sie zeigt BELEGE, nicht Zeilen. Ein Beleg ohne seine Gegenbuchung ist keine
 * halbe Information, sondern eine falsche: Soll allein sieht nach einem
 * unausgeglichenen Buch aus. Deshalb blättert auch die Schnittstelle über
 * Buchungen - würde die Oberfläche Zeilen holen und selbst gruppieren, zerrisse
 * jede Seitengrenze einen Beleg, und der letzte auf der Seite sähe immer falsch
 * aus.
 *
 * Hier hängt auch der Storno. Eine gebuchte Zeile wird nicht gelöscht und nicht
 * geändert - sie wird durch eine Gegenbuchung aufgehoben, und beide bleiben
 * stehen.
 */

const SEITE = 25;

/** Der erste Tag des laufenden Jahres, als JJJJ-MM-TT. */
function jahresanfang() {
  const jetzt = new Date();
  return `${jetzt.getFullYear()}-01-01`;
}

export function JournalPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [von, setVon] = useState(jahresanfang);
  const [bis, setBis] = useState('');
  const [konto, setKonto] = useState('');
  const [seite, setSeite] = useState(0);
  const [stornoZiel, setStornoZiel] = useState(null);
  const [grund, setGrund] = useState('');
  const [fehler, setFehler] = useState(null);

  const kontenQuery = useQuery({
    queryKey: ['finance', 'accounts', 'alle'],
    queryFn: () => financeApi.listAccounts({ limit: 300 }).then((r) => r.items || []),
  });

  const filter = useMemo(
    () => ({
      ...(von ? { from: von } : {}),
      ...(bis ? { to: bis } : {}),
      ...(konto ? { accountId: konto } : {}),
      limit: SEITE,
      offset: seite * SEITE,
    }),
    [von, bis, konto, seite],
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ['finance', 'journals', filter],
    queryFn: () => financeApi.listJournals(filter),
    placeholderData: keepPreviousData,
    /* Ist ein Konto gewählt, tritt das Kontoblatt an die Stelle der
       Belegliste - dann wird diese Abfrage gar nicht erst gestellt. */
    enabled: !konto,
  });

  /**
   * Das Kontoblatt.
   *
   * Wer ein Konto auswählt, will nicht die Belege sehen, in denen es
   * vorkommt, sondern das Konto: Vortrag, Bewegungen, laufender Saldo,
   * Schluss. Ohne Vortrag wäre der Schlusssaldo nicht der Kontostand,
   * sondern nur die Bewegung des Zeitraums - eine Zahl, die aussieht wie ein
   * Saldo und keiner ist.
   */
  const blattFilter = useMemo(
    () => ({ ...(von ? { from: von } : {}), ...(bis ? { to: bis } : {}) }),
    [von, bis],
  );
  const blatt = useQuery({
    queryKey: ['finance', 'account-sheet', konto, blattFilter],
    queryFn: () => financeApi.accountSheet(konto, blattFilter).then((r) => r.items),
    enabled: Boolean(konto),
    placeholderData: keepPreviousData,
  });

  const belege = data?.items || [];
  /* Die echte Gesamtzahl kommt aus der Antwort. items.length sagte nur, wie
     viel gerade heruntergeladen wurde - bei 25 pro Seite immer 25. */
  const gesamt = data?.meta?.total ?? 0;
  const letzteSeite = Math.max(0, Math.ceil(gesamt / SEITE) - 1);

  const storno = useMutation({
    mutationFn: ({ id, reason }) => financeApi.reverseJournal(id, reason),
    onSuccess: () => {
      setStornoZiel(null);
      setGrund('');
      setFehler(null);
      queryClient.invalidateQueries({ queryKey: ['finance', 'journals'] });
      queryClient.invalidateQueries({ queryKey: ['finance', 'accounts'] });
    },
    onError: (e) => setFehler(e.message),
  });

  /* Die Belegsumme ist die Sollseite - Soll und Haben sind gleich, sonst wäre
     die Buchung nie durchgegangen. */
  const belegsumme = (beleg) => beleg.lines
    .filter((z) => z.type === 'DEBIT')
    .reduce((a, z) => a + Number(z.amount), 0);

  /* Ein Beleg, der zu einem Dokument gehört, wird am Dokument korrigiert, und
     die Schnittstelle weist einen Storno hier ohnehin ab. Den Knopf gar nicht
     erst anzubieten ist ehrlicher, als ihn ins Leere laufen zu lassen. */
  const stornierbar = (beleg) => !beleg.reversedBy && !beleg.referenceType;

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('journal.title')}</h1>
          <p className="muted">{t('journal.subtitle')}</p>
        </div>
      </header>

      <div className="toolbar">
        <label className="field">
          <span className="field-label">{t('journal.filter.from')}</span>
          <input
            className="input"
            type="date"
            value={von}
            onChange={(e) => { setVon(e.target.value); setSeite(0); }}
          />
        </label>
        <label className="field">
          <span className="field-label">{t('journal.filter.to')}</span>
          <input
            className="input"
            type="date"
            value={bis}
            onChange={(e) => { setBis(e.target.value); setSeite(0); }}
          />
        </label>
        <label className="field">
          <span className="field-label">{t('journal.filter.account')}</span>
          <select
            className="select"
            value={konto}
            onChange={(e) => { setKonto(e.target.value); setSeite(0); }}
          >
            <option value="">{t('journal.filter.allAccounts')}</option>
            {(kontenQuery.data || []).map((k) => (
              <option key={k.id} value={k.id}>{`${k.code} ${k.name}`}</option>
            ))}
          </select>
        </label>
      </div>

      {error && <div className="alert danger">{error.message}</div>}
      {blatt.error && <div className="alert danger">{blatt.error.message}</div>}

      {konto ? (
        <article className="card">
          {blatt.isLoading && <Skeleton variant="table" />}
          {blatt.data && (
            <>
              <header className="journal-voucher-head">
                <div className="journal-voucher-id">
                  <strong className="mono">{blatt.data.account.code}</strong>
                  <span>{blatt.data.account.name}</span>
                </div>
                <div className="journal-voucher-marks">
                  <span className="muted text-sm">
                    {t('journal.sheet.opening')} <span className="mono num">{formatCurrency(blatt.data.openingBalance)}</span>
                  </span>
                  <span className="mono num">
                    {t('journal.sheet.closing')} {formatCurrency(blatt.data.closingBalance)}
                  </span>
                </div>
              </header>
              {/* Eine gekürzte Liste zeigte einen Schlusssaldo, der keiner
                  ist. Lieber laut sagen als still abschneiden. */}
              {blatt.data.truncated && (
                <div className="alert warning">{t('journal.sheet.truncated')}</div>
              )}
              <div className="table-wrap">
                <table className="table journal-lines">
                  <thead>
                    <tr>
                      <th>{t('col.date')}</th>
                      <th>{t('col.number')}</th>
                      <th>{t('journal.sheet.counterAccount')}</th>
                      <th>{t('col.description')}</th>
                      <th className="num">{t('journal.col.debit')}</th>
                      <th className="num">{t('journal.col.credit')}</th>
                      <th className="num">{t('journal.sheet.running')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="journal-opening">
                      <td colSpan={6}>{t('journal.sheet.openingRow')}</td>
                      <td className="num">{formatCurrency(blatt.data.openingBalance)}</td>
                    </tr>
                    {blatt.data.lines.map((z) => (
                      <tr key={z.id}>
                        <td>{formatDate(z.postedDate)}</td>
                        <td className="mono">{z.journalNumber}</td>
                        <td>
                          {/* Bei mehr als einem Gegenkonto steht ihre ZAHL da.
                              Eines herauszugreifen wäre eine Aussage über den
                              Beleg, die nicht stimmt. */}
                          {z.counterLineCount > 1
                            ? t('journal.sheet.split', { count: z.counterLineCount })
                            : z.counterAccounts || '-'}
                        </td>
                        <td>{z.description}</td>
                        <td className="num">{z.type === 'DEBIT' ? formatCurrency(z.amount) : ''}</td>
                        <td className="num">{z.type === 'CREDIT' ? formatCurrency(z.amount) : ''}</td>
                        <td className="num">{formatCurrency(z.runningBalance)}</td>
                      </tr>
                    ))}
                    <tr className="journal-closing">
                      <td colSpan={4}>{t('journal.sheet.closingRow')}</td>
                      <td className="num">{formatCurrency(blatt.data.debitTotal)}</td>
                      <td className="num">{formatCurrency(blatt.data.creditTotal)}</td>
                      <td className="num">{formatCurrency(blatt.data.closingBalance)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
        </article>
      ) : null}

      {!konto && isLoading && <Skeleton variant="table" />}

      {!konto && !isLoading && belege.length === 0 && <EmptyState message={t('journal.empty')} />}

      {!konto && belege.map((beleg) => (
        <article className="card journal-voucher" key={beleg.id}>
          <header className="journal-voucher-head">
            <div className="journal-voucher-id">
              <strong className="mono">{beleg.journalNumber}</strong>
              <span className="muted">{formatDate(beleg.postedDate)}</span>
            </div>
            <div className="journal-voucher-marks">
              {beleg.isReversal && <Badge variant="warning">{t('journal.badge.isReversal')}</Badge>}
              {beleg.reversedBy && (
                <Badge variant="neutral">
                  {t('journal.badge.reversedBy', { number: beleg.reversedBy.journalNumber })}
                </Badge>
              )}
              {beleg.referenceType && <Badge variant="info">{beleg.referenceType}</Badge>}
              <span className="mono num">{formatCurrency(belegsumme(beleg))}</span>
              {stornierbar(beleg) && (
                <Button
                  size="sm"
                  variant="default"
                  onClick={() => { setStornoZiel(beleg); setGrund(''); setFehler(null); }}
                >
                  {t('journal.action.reverse')}
                </Button>
              )}
            </div>
          </header>
          <p className="journal-voucher-text">{beleg.description}</p>
          <div className="table-wrap">
            <table className="table journal-lines">
              <thead>
                <tr>
                  <th>{t('journal.col.account')}</th>
                  <th className="num">{t('journal.col.debit')}</th>
                  <th className="num">{t('journal.col.credit')}</th>
                </tr>
              </thead>
              <tbody>
                {beleg.lines.map((z) => (
                  <tr key={z.id}>
                    <td><span className="mono">{z.accountCode}</span> {z.accountName}</td>
                    <td className="num">{z.type === 'DEBIT' ? formatCurrency(z.amount) : ''}</td>
                    <td className="num">{z.type === 'CREDIT' ? formatCurrency(z.amount) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>
      ))}

      {!konto && gesamt > SEITE && (
        <div className="audit-pagination">
          <Button size="sm" disabled={seite === 0} onClick={() => setSeite((s) => Math.max(0, s - 1))}>
            {t('journal.pager.previous')}
          </Button>
          <span className="muted text-sm">
            {t('journal.pager.position', {
              from: seite * SEITE + 1,
              to: Math.min(gesamt, (seite + 1) * SEITE),
              total: gesamt,
            })}
          </span>
          <Button size="sm" disabled={seite >= letzteSeite} onClick={() => setSeite((s) => s + 1)}>
            {t('journal.pager.next')}
          </Button>
        </div>
      )}

      <Modal
        open={Boolean(stornoZiel)}
        onClose={() => { setStornoZiel(null); setFehler(null); }}
        title={t('journal.reverse.title', { number: stornoZiel?.journalNumber || '' })}
        footer={(
          <>
            <Button onClick={() => { setStornoZiel(null); setFehler(null); }}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              disabled={storno.isPending}
              onClick={() => storno.mutate({ id: stornoZiel.id, reason: grund })}
            >
              {t('journal.action.reverse')}
            </Button>
          </>
        )}
      >
        {/* Der Satz, der erklärt, was gleich passiert - und was nicht. */}
        <p>{t('journal.reverse.explain', { date: formatDate(stornoZiel?.postedDate) })}</p>
        <label className="field">
          <span className="field-label">{t('journal.reverse.reason')}</span>
          <input
            className="input"
            value={grund}
            maxLength={300}
            onChange={(e) => setGrund(e.target.value)}
            placeholder={t('journal.reverse.reasonHint')}
          />
        </label>
        {fehler && <div className="alert danger">{fehler}</div>}
      </Modal>
    </section>
  );
}
